import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useStudio } from "@/context/StudioContext";
import { CaretUpDown, Plus, Buildings, Check } from "@phosphor-icons/react";

export function StudioSwitcher({ onNavigate }) {
  const { studios, active, switchStudio } = useStudio();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (path) => { setOpen(false); onNavigate?.(); navigate(path); };

  return (
    <div className="relative" ref={ref}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-600 px-1 mb-1.5">My Choice Studios</div>
      <button data-testid="studio-switcher" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 rounded-xl border border-white/8 bg-[#121212] px-3 py-2.5 hover:border-white/20 transition-colors">
        <span className="h-8 w-8 rounded-lg flex items-center justify-center text-lg shrink-0"
          style={{ background: `${active.color}22`, border: `1px solid ${active.color}55` }}>{active.emoji}</span>
        <div className="min-w-0 flex-1 text-left">
          <div className="font-display font-bold text-white text-sm truncate">{active.name}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Active studio</div>
        </div>
        <CaretUpDown size={16} className="text-zinc-500 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full glass rounded-xl p-1.5 shadow-2xl animate-fadeUp">
          <div className="max-h-64 overflow-y-auto">
            {studios.map((s) => (
              <button key={s.id} data-testid={`switch-${s.id}`} onClick={() => { switchStudio(s.id); setOpen(false); onNavigate?.(); }}
                className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 hover:bg-white/5 transition-colors text-left">
                <span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: `${s.color}22` }}>{s.emoji}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{s.name}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{s.genre || "Studio"}</div>
                </div>
                {s.id === active.id && <Check size={15} weight="bold" className="text-[#00E5FF]" />}
              </button>
            ))}
          </div>
          <div className="border-t border-white/8 mt-1.5 pt-1.5">
            <button data-testid="switcher-overview" onClick={() => go("/studios")} className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
              <Buildings size={16} weight="duotone" className="text-[#00E5FF]" /> All Studios Dashboard
            </button>
            <button data-testid="switcher-create" onClick={() => go("/studios/new")} className="w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-white hover:bg-white/5 transition-colors">
              <Plus size={16} weight="bold" className="text-[#FF007F]" /> Create New Studio
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
